import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { COURSES, SITE } from "@/site";
import CTABanner from "@/components/sections/CTABanner";
import LeadFormCompact from "@/components/sections/LeadFormCompact";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEO, { courseSchema, faqSchema, breadcrumbSchema } from "@/components/SEO";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

// Default FAQ set applied to any course (can be overridden per slug)
const genericFaqs = (name) => ([
    { q: `What is the right age to start ${name.toLowerCase()}?`, a: `We accept students from age 6 and above for ${name.toLowerCase()}. The instrument, student's physical development and interest level all play a role — our faculty will guide you at the free trial class.` },
    { q: `How long does it take to see progress?`, a: `With consistent practice (20–30 minutes, 5 days a week), most beginners feel comfortable within 2–3 months and reach their first exam grade within 8–12 months.` },
    { q: `Do I need my own instrument?`, a: `For most instruments, yes — you'll need one at home for daily practice. Our faculty can recommend budget-friendly options. Production and DJ students use studio equipment on-site.` },
    { q: `Are classes one-on-one or in groups?`, a: `Instrument classes are typically one-on-one or 2-student small batches. Dance and theory classes run in small groups capped at 8 students.` },
    { q: `Will I get to perform?`, a: `Absolutely. Every student performs on stage at least 2–3 times a year through our termly recitals, annual concert and monthly jam nights.` },
]);

const generalIntro = (c) => ({
    instrument: `Ihao Music House Delhi offers structured ${c.name.toLowerCase()} classes in Malviya Nagar, South Delhi — just opposite Select Citywalk & DLF Avenue Saket. Whether you are a complete beginner, a returning adult learner, or an exam-focused student, our certified faculty will build a personalised journey for you.`,
    vocal: `Our vocal training programme at Ihao Music House blends Western classical foundations with contemporary and playback styles. You'll work on breath control, pitch accuracy, diction, tone colour and stage presence — from Grade 1 beginner all the way to performance-ready.`,
    production: `Our ${c.name.toLowerCase()} course at Ihao Music House is built for creators who want to ship music and run events. You'll work on industry-standard hardware and software with experienced producers and engineers guiding you every step of the way.`,
    dance: `Our dance classes at Ihao Music House are performance-first and style-diverse — from hip-hop foundations to contemporary lyricism, jazz and classical technique. Classes run in batches for kids, teens and adults in a dedicated, mirrored dance studio.`,
});

const curriculum = {
    instrument: [
        "Correct technique, posture and hand position",
        "Music reading — notation, rhythm and timing",
        "Scales, chords, arpeggios and theory",
        "Repertoire across classical, jazz, pop and film music",
        "Ear training and improvisation basics",
        "Grade exam preparation — ABRSM, Trinity or Rockschool",
    ],
    vocal: [
        "Breath control and diaphragmatic support",
        "Pitch accuracy, intervals and solfège",
        "Vocal warm-ups and cool-downs",
        "Tone, resonance and dynamic control",
        "Western classical and contemporary repertoire",
        "Microphone technique and stage performance",
    ],
    production: [
        "Digital Audio Workstation workflow (Logic Pro / Ableton / FL)",
        "Sound design — synths, sampling, drum programming",
        "Arrangement, mixing and mastering basics",
        "Industry-standard plugins and signal flow",
        "Live performance / DJ sets on Pioneer CDJs",
        "Release-ready track production",
    ],
    dance: [
        "Warm-up, conditioning and flexibility work",
        "Foundations of hip-hop, popping and locking",
        "Contemporary technique and floor work",
        "Choreography and performance routines across styles",
        "Rhythm, musicality and formation skills",
        "Stage performance and recital preparation",
    ],
};

const benefits = [
    "Certified international curriculum (Rockschool, ABRSM, Trinity where applicable)",
    "Small batch sizes — one-on-one or 2-student instrument classes",
    "Termly recitals, annual concerts and monthly jam nights",
    "Acoustic-treated studio with premium instruments",
    "Structured weekly practice plans — you always know what to work on",
    "Personal attention from conservatory-trained, performance-active faculty",
];

export default function CourseDetail() {
    const { slug } = useParams();
    const course = COURSES.find((c) => c.slug === slug);
    if (!course) return <Navigate to="/courses" replace />;

    const faqs = genericFaqs(course.name);
    const intro = generalIntro(course)[course.category];
    const curr = curriculum[course.category];

    const schema = [
        courseSchema(course),
        faqSchema(faqs),
        breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Courses", path: "/courses" },
            { name: course.name, path: `/courses/${course.slug}` },
        ]),
    ];

    return (
        <div data-testid={`page-course-${slug}`}>
            <SEO title={course.title} description={course.metaDesc} path={`/courses/${course.slug}`} image={course.image} schema={schema} />
            <Breadcrumbs items={[
                { label: "Home", to: "/" },
                { label: "Courses", to: "/courses" },
                { label: course.name },
            ]} />

            {/* HERO */}
            <section className="relative min-h-[75vh] flex items-center overflow-hidden">
                <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${course.hero || course.image})` }} aria-hidden />
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/55 to-black" aria-hidden />

                <div className="relative max-w-5xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-24 md:py-32">
                    <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-gold mb-6 border border-gold/30 rounded-full px-4 py-1.5 bg-black/30 backdrop-blur">
                        <span className="w-1.5 h-1.5 rounded-full bg-gold" />
                        Limited Seats · All Levels
                    </span>
                    <h1 className="font-display font-bold text-white text-balance leading-[1.05] text-4xl sm:text-5xl md:text-6xl">
                        {course.h1}
                    </h1>
                    <p className="mt-5 text-white/75 text-base md:text-lg max-w-2xl leading-relaxed">{course.blurb}</p>
                    <div className="mt-8 flex flex-col sm:flex-row gap-3">
                        <Link to="/contact" data-testid="course-hero-book-demo" className="inline-flex items-center justify-center bg-gold text-black font-semibold text-sm px-8 py-4 rounded-full hover:scale-[1.03] hover:bg-gold-hover transition-all duration-300 gold-glow-strong">Book Free Trial Class</Link>
                        <a href={`tel:${SITE.phoneTel}`} className="inline-flex items-center justify-center border border-white/25 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white/5 transition-colors">Call Studio</a>
                    </div>
                </div>
            </section>

            {/* INTRO + SIDEBAR */}
            <section className="relative py-20 md:py-28 bg-background">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-12">
                    <div className="lg:col-span-2">
                        <span className="text-[10px] tracking-[0.3em] uppercase text-gold/80">Course Overview</span>
                        <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-white leading-tight">
                            {course.name} training built on structure, practice and performance.
                        </h2>
                        <p className="mt-6 text-white/70 leading-relaxed">{intro}</p>
                        <p className="mt-4 text-white/70 leading-relaxed">
                            Our studio is located directly opposite <strong className="text-white">Select Citywalk / DLF Avenue Saket</strong>, making it easily accessible from Malviya Nagar, Saket, Hauz Khas, Greater Kailash and Panchsheel Park. Weekly classes, written practice plans, termly recitals — everything aligned to help you improve fast without losing the joy of music.
                        </p>

                        <h3 className="mt-12 font-display text-xl md:text-2xl text-white font-semibold">What you will learn</h3>
                        <ul className="mt-5 space-y-3 text-white/75">
                            {curr.map((p, i) => (
                                <li key={i} className="flex gap-3 text-sm leading-relaxed"><span className="text-gold mt-1">—</span>{p}</li>
                            ))}
                        </ul>

                        <h3 className="mt-12 font-display text-xl md:text-2xl text-white font-semibold">Who this course is for</h3>
                        <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
                            {[
                                { t: "Kids (6–12)", b: "Fun, age-appropriate introduction with games, songs and gentle technical building blocks." },
                                { t: "Teens & Adults", b: "Grade-track or leisure-track — we build around your goals, experience and schedule." },
                                { t: "Advanced Students", b: "Grade 5+ prep, diploma preparation, repertoire building and performance masterclasses." },
                            ].map((x) => (
                                <div key={x.t} className="rounded-xl border border-white/10 bg-[#121212] p-5">
                                    <div className="font-display text-white font-semibold">{x.t}</div>
                                    <p className="mt-2 text-xs text-white/60 leading-relaxed">{x.b}</p>
                                </div>
                            ))}
                        </div>

                        <h3 className="mt-12 font-display text-xl md:text-2xl text-white font-semibold">Why students choose Ihao for {course.name.toLowerCase()}</h3>
                        <ul className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-3 text-white/75">
                            {benefits.map((b, i) => (
                                <li key={i} className="flex gap-3 text-sm leading-relaxed rounded-xl border border-white/10 bg-[#121212] p-4"><span className="text-gold mt-0.5">✦</span>{b}</li>
                            ))}
                        </ul>

                        <h3 className="mt-12 font-display text-xl md:text-2xl text-white font-semibold">Location — easily reachable from across South Delhi</h3>
                        <p className="mt-4 text-white/70 leading-relaxed">
                            Ihao Music House is in the heart of <strong className="text-white">Malviya Nagar, South Delhi</strong> — opposite Select Citywalk Saket. Students come to us from <Link to="/locations/saket" className="text-gold border-b border-gold/30">Saket</Link>, <Link to="/locations/hauz-khas" className="text-gold border-b border-gold/30">Hauz Khas</Link>, <Link to="/locations/greater-kailash" className="text-gold border-b border-gold/30">Greater Kailash</Link>, Panchsheel, Green Park, GK-I and GK-II. If you're searching for {course.h1.toLowerCase()}, you'll find us among the highest-rated music schools in the area.
                        </p>
                    </div>

                    <aside className="rounded-2xl border border-gold/30 bg-gradient-to-br from-[#1a1405] to-[#0a0a0a] p-8 h-fit lg:sticky lg:top-28">
                        <span className="text-[10px] tracking-[0.3em] uppercase text-gold">Class details</span>
                        <h3 className="mt-3 font-display text-2xl text-white font-semibold">{course.name}</h3>
                        <p className="mt-1 text-xs text-gold/80 uppercase tracking-[0.2em]">{course.short}</p>
                        <dl className="mt-6 space-y-4 text-sm">
                            <div className="flex justify-between border-b border-white/10 pb-3"><dt className="text-white/50">Levels</dt><dd className="text-white font-medium text-right">{course.levels.join(" · ")}</dd></div>
                            <div className="flex justify-between border-b border-white/10 pb-3"><dt className="text-white/50">Format</dt><dd className="text-white font-medium">1-on-1 / Small batch</dd></div>
                            <div className="flex justify-between border-b border-white/10 pb-3"><dt className="text-white/50">Duration</dt><dd className="text-white font-medium">45–60 min</dd></div>
                            <div className="flex justify-between border-b border-white/10 pb-3"><dt className="text-white/50">Frequency</dt><dd className="text-white font-medium">2× / week</dd></div>
                            <div className="flex justify-between border-b border-white/10 pb-3"><dt className="text-white/50">Location</dt><dd className="text-white font-medium">Malviya Nagar</dd></div>
                            <div className="flex justify-between"><dt className="text-white/50">Syllabus</dt><dd className="text-white font-medium">International</dd></div>
                        </dl>
                        <Link to="/contact" className="mt-7 inline-flex w-full items-center justify-center bg-gold text-black font-semibold text-sm px-6 py-3.5 rounded-full hover:bg-gold-hover transition-colors">Book Free Trial Class</Link>
                        <a href={SITE.whatsapp} target="_blank" rel="noreferrer" className="mt-3 inline-flex w-full items-center justify-center border border-white/20 text-white font-semibold text-sm px-6 py-3.5 rounded-full hover:bg-white/5 transition-colors">WhatsApp Studio</a>
                    </aside>
                </div>
            </section>

            {/* FAQ */}
            <section className="relative py-20 md:py-28 bg-black">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-10">
                        <span className="text-[10px] tracking-[0.3em] uppercase text-gold/80">FAQ</span>
                        <h2 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-white leading-tight">{course.name} — frequently asked questions.</h2>
                    </div>
                    <Accordion type="single" collapsible className="space-y-3">
                        {faqs.map((f, i) => (
                            <AccordionItem key={i} value={`cfaq-${i}`} data-testid={`course-faq-${i}`} className="border border-white/10 rounded-2xl bg-[#121212] px-6">
                                <AccordionTrigger className="text-white font-display text-base md:text-lg font-semibold text-left hover:no-underline py-5">{f.q}</AccordionTrigger>
                                <AccordionContent className="text-white/65 leading-relaxed pb-5">{f.a}</AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* RELATED */}
            <section className="relative py-16 bg-background">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h3 className="font-display text-xl md:text-2xl text-white font-semibold mb-6">Explore other courses</h3>
                    <div className="flex gap-4 overflow-x-auto pb-3 snap-x">
                        {COURSES.filter((c) => c.slug !== slug).map((c) => (
                            <Link key={c.slug} to={`/courses/${c.slug}`} className="snap-start min-w-[220px] rounded-xl border border-white/10 bg-[#121212] p-5 hover:border-gold/40 transition-colors">
                                <div className="text-gold text-[10px] uppercase tracking-[0.2em]">{c.short}</div>
                                <div className="mt-2 font-display text-lg text-white font-semibold">{c.name}</div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            <LeadFormCompact />

            <CTABanner />
        </div>
    );
}
